// src/services/auth.ts
import Keycloak from 'keycloak-js';
import type { KeycloakInstance, KeycloakError } from 'keycloak-js';

interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

class KeycloakAuth {
  private keycloak: KeycloakInstance | null = null;
  private isAuthenticated: boolean = false;
  private isInitialized: boolean = false;

  constructor() {
    this.keycloak = null;
    this.isAuthenticated = false;
    this.isInitialized = false;
  }

  async init(): Promise<boolean> {
    // Check for token from ecosystem 1 first
    const urlParams = new URLSearchParams(window.location.search);
    const externalToken = urlParams.get('t');
    const idToken = urlParams.get('i');

    // Ensure Keycloak instance is created
    if (!this.keycloak) {
      this.initializeKeycloakInstance();
    }

    if (externalToken) {
      try {
        // Use token from ecosystem 1 without initializing Keycloak normally
        const success = await this.handleExternalToken(externalToken, idToken);
        if (success) {
          // Clean URL after successful token usage
          window.history.replaceState({}, document.title, window.location.pathname);
          return true;
        }
      } catch (error) {
        console.error('External token handling failed:', error);
      }
    }

    // Try to use stored tokens
    const storedToken = sessionStorage.getItem('keycloakToken');
    if (storedToken && !this.isTokenExpired(storedToken)) {
      return await this.initializeWithStoredToken(storedToken);
    }

    // No valid tokens found, don't auto-initialize Keycloak to prevent redirects
    return false;
  }

  private initializeKeycloakInstance(): void {
    const config: KeycloakConfig = {
      url: process.env.REACT_APP_KEYCLOAK_URL || '',
      realm: process.env.REACT_APP_KEYCLOAK_REALM || '',
      clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || '',
    };

    this.keycloak = new Keycloak(config);
    
    // Add error handler to Keycloak instance
    this.keycloak.onAuthError = (error: KeycloakError) => {
      console.error('Keycloak auth error:', error);
    };
  }

  private async handleExternalToken(token: string, idToken?: string|null): Promise<boolean> {
    try {
      // Create Keycloak instance but DON'T initialize it normally
      const config: KeycloakConfig = {
        url: process.env.REACT_APP_KEYCLOAK_URL || '',
        realm: process.env.REACT_APP_KEYCLOAK_REALM || '',
        clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || '',
      };

      this.keycloak = new Keycloak(config);
      
      // Manually set the token and mark as authenticated
      this.keycloak.token = token;
      this.keycloak.authenticated = true;
      
      // Store the token
      sessionStorage.setItem('keycloakToken', token);
      sessionStorage.setItem('keycloakIdToken', idToken || '');
      this.isAuthenticated = true;
      this.isInitialized = true;

      // Try to get user info to validate the token
      const userInfo = this.getUserInfo();
      if (userInfo) {
        console.log('Authenticated as:', userInfo.name);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to handle external token:', error);
      return false;
    }
  }

  private async initializeWithStoredToken(token: string): Promise<boolean> {
    try {
      const config: KeycloakConfig = {
        url: process.env.REACT_APP_KEYCLOAK_URL || '',
        realm: process.env.REACT_APP_KEYCLOAK_REALM || '',
        clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || '',
      };

      this.keycloak = new Keycloak(config);
      this.keycloak.token = token;
      this.keycloak.authenticated = true;

      // Validate the token is still valid
      if (this.isTokenExpired(token)) {
        sessionStorage.removeItem('keycloakToken');
        this.keycloak = null;
        return false;
      }

      this.isAuthenticated = true;
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Failed to initialize with stored token:', error);
      return false;
    }
  }

  async login(): Promise<boolean> {
    try {
      // Only initialize Keycloak when user explicitly clicks login
      const config: KeycloakConfig = {
        url: process.env.REACT_APP_KEYCLOAK_URL || '',
        realm: process.env.REACT_APP_KEYCLOAK_REALM || '',
        clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || '',
      };

      this.keycloak = new Keycloak(config);

      // Initialize with login-required to trigger redirect
      await this.keycloak.init({
        onLoad: 'login-required', // This will redirect to Keycloak
        pkceMethod: 'S256'
      });

      this.isAuthenticated = true;
      this.isInitialized = true;
      this.storeTokens();
      
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  }

  private storeTokens(): void {
    if (!this.keycloak) return;

    if (this.keycloak.token) {
      sessionStorage.setItem('keycloakToken', this.keycloak.token);
    }
    if (this.keycloak.refreshToken) {
      sessionStorage.setItem('keycloakRefreshToken', this.keycloak.refreshToken);
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      const bufferTime = 30000; // 30 seconds buffer
      
      return expirationTime - bufferTime < currentTime;
    } catch {
      return true;
    }
  }

  async getToken(): Promise<string> {
    if (!this.keycloak || !this.keycloak.token) {
      throw new Error('Not authenticated');
    }

    // Check if token is expired or about to expire
    if (this.isTokenExpired(this.keycloak.token)) {
      // For external tokens, we can't refresh them, so need to reauthenticate
      sessionStorage.removeItem('keycloakToken');
      this.isAuthenticated = false;
      throw new Error('Token expired, please login again');
    }

    return this.keycloak.token;
  }

  getUserInfo(): { id: string; name: string; email: string; roles: string[] } | null {
    const token = this.keycloak?.token || sessionStorage.getItem('keycloakToken');
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        id: payload.sub,
        name: payload.name || payload.preferred_username || 'User',
        email: payload.email || '',
        roles: payload.realm_access?.roles || []
      };
    } catch {
      return null;
    }
  }

  getIsAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  async logout(): Promise<void> {
    this.isAuthenticated = false;
    this.isInitialized = false;
    this.redirectToKeycloakLogout();
  }

  private redirectToKeycloakLogout(): void {
    const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL;
    const realm = process.env.REACT_APP_KEYCLOAK_REALM;
    if (keycloakUrl && realm) {
      // Get the ID token from storage for better logout
      const idToken = sessionStorage.getItem('keycloakIdToken');
      
      let logoutUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout`;
      if (idToken) {
        logoutUrl += `?id_token_hint=${idToken}&post_logout_redirect_uri=${encodeURIComponent(process.env.REACT_APP_PHOTIZO_URL || "")}`;
      } else {
        logoutUrl += `?redirect_uri=${encodeURIComponent(process.env.REACT_APP_PHOTIZO_URL || "")}`;
      }
      this.clearLocalStorage();
      window.location.href = logoutUrl;
    } else {
      // Just reload the page if Keycloak config is missing
      window.location.href = '/';
    }
  }

  private clearLocalStorage(): void {
    sessionStorage.clear();
    localStorage.clear();
  }

  getDebugInfo(): any {
    return {
      keycloakInstance: !!this.keycloak,
      isInitialized: this.isInitialized,
      isAuthenticated: this.isAuthenticated,
      hasToken: !!sessionStorage.getItem('keycloakToken'),
      keycloakMethods: this.keycloak ? Object.keys(this.keycloak) : []
    };
  }
}

export const keycloakAuth = new KeycloakAuth();
export default keycloakAuth;